import './ceremony.less';

import * as _ from 'lodash';
import { ConvertCeremonyModel } from './convert/ConvertCeremonyModel';
import { AttachCeremonyModel } from './attach/AttachCeremonyComponent';
import { TromanError } from '../../../common/TromanError';
import { ceremonyStates } from '../../objects/Ceremony';
import { formatDurationMinutes, getClaimTypeText } from '../../misc/textHelper';
import { claimTypes } from '../../objects/Claim';
import { auditIncidents } from '../../objects/Audit';

/**
 * 
 * @typedef { import('../../objects/Ceremony').CeremonyDetails } CeremonyDetails
 * @typedef { import('../../objects/Claim').ClaimNugget } ClaimNugget
 */

const OPTION_NEW = 'new';
const OPTION_EXISTING = 'existing';
const OPTION_REJECT = 'reject';

/**
 * @param {ClaimNugget} nugget
 */
function getNuggetDetail(nugget) {
    switch (nugget.ClaimType) {
        case claimTypes.travel:
            return `${nugget.DrivingDistance?.toFixed(0)} km, ${nugget.RouteFrom} till ${nugget.RouteTo}`;
        case claimTypes.parking:
            return `${nugget.Parking?.toFixed(2)} kr`;
        case claimTypes.congestion:
            return `${nugget.Congestion?.toFixed(2)} kr`;
        case claimTypes.publicTransport:
            return `${nugget.PublicTransport?.toFixed(2)} kr`;
        case claimTypes.travelTime:
            return formatDurationMinutes(nugget.TravelTime);
        case claimTypes.expense:
            return `${nugget.Amount?.toFixed(2)} kr, ${nugget.Purpose}`;
        case claimTypes.lostIncomeAmount:
            return `${nugget.Amount?.toFixed(2)} kr, ${nugget.Employer}`;
        case claimTypes.lostIncomeNumber:
            return `${nugget.Hours?.toFixed(2)} tim, ${nugget.Employer}`;
        case claimTypes.lostIncomeRate:
            return `${nugget.Hours?.toFixed(2)} tim à ${nugget.HourlyRate?.toFixed(2)}, ${nugget.Employer}`;
            
        case claimTypes.legacyTravel: {
            let parts = [];
            if (nugget.DrivingDistance) {
                parts.push(`Km-ersättning: ${nugget.DrivingDistance?.toFixed(0)} km, ${nugget.RouteFrom} till ${nugget.RouteTo}`);
            }
            if (nugget.Parking) {
                parts.push(`Parkering: ${nugget.Parking?.toFixed(2)} kr`);
            }
            if (nugget.Congestion) {
                parts.push(`Trängselskatt: ${nugget.Congestion?.toFixed(2)} kr`);
            }
            if (nugget.PublicTransport) {
                parts.push(`Kollektivtrafik: ${nugget.PublicTransport?.toFixed(2)} kr`);
            }
            if (nugget.TravelTime) {
                parts.push(`Restid: ${formatDurationMinutes(nugget.TravelTime)}`);
            }
            return parts.join(', ');
        }
    }
}

angular.module("tromanWeb").controller('CeremonyController', ['$q', 'stateService', 'tromanServer', 'ObjectCache', 'dialogService', 'downloadService', 'eventId', function ($qservice, stateService, tromanServer, ObjectCache, dialogService, downloadService, eventId) {
    this._isSaving = false;
    this.targetSelection = OPTION_EXISTING;
    this.parentForm = null;
    this.newform = null;
    this.existingform = null;
    this.rejectform = null;
    this.previousRejection = null;
    this.rejectionMessage = '';
    /** @type {CeremonyDetails} */
    this.ceremony = null;
    /** @type {ConvertCeremonyModel} */
    this.convertCeremonyModel = null;
    /** @type {AttachCeremonyModel} */
    this.attachCeremonyModel = null;

    this.canSave = function () {
        if (this._isSaving) return false;
        if (this.ceremony == null) return false;

        if (this.targetSelection === OPTION_NEW) {
            return this.convertCeremonyModel != null && this.newform?.$valid;
        } else if (this.targetSelection === OPTION_EXISTING) {
            return this.attachCeremonyModel != null && this.attachCeremonyModel.isValid && this.existingform != null && this.existingform.$valid;
        } else if (this.targetSelection === OPTION_REJECT) {
            return this.rejectform?.$valid;
        }
    };

    this._saveNew = function () {
        this._isSaving = true;
        tromanServer.Ceremonies
            .convertCeremony(this.ceremony.EventId, this.convertCeremonyModel.createAdd()) // TODO
            .then(meetingId => {
                stateService.forceGo('meeting.payroll', { meetingId: meetingId }, { reload: true, location: 'replace' });
            })
            .catch(dialogService.error)
            .finally(() => this._isSaving = false);
    };

    this._saveExisting = function () {
        this._isSaving = true;
        tromanServer.Ceremonies
            .attachCeremony(this.ceremony.EventId, this.attachCeremonyModel.createAttach())
            .then(meetingId => {
                stateService.forceGo('meeting.payroll', { meetingId: meetingId }, { reload: true, location: 'replace' });
            })
            .catch(dialogService.error)
            .finally(() => this._isSaving = false);
    };

    this._reject = function () {
        this._isSaving = true;
        tromanServer.Ceremonies
            .rejectCeremony(this.ceremony.EventId, {
                RejectionMessage: this.rejectionMessage,
            })
            .then(() => {
                stateService.forceGo('meetings.ceremony', {}, { reload: true, location: 'replace' });
            })
            .catch(dialogService.error)
            .finally(() => this._isSaving = false);
    };

    this.save = function save() {
        this.parentForm.$commitViewValue();
        this.parentForm.$setSubmitted();

        if (!this.canSave()) return;

        if (this.targetSelection === OPTION_NEW) {
            this._saveNew();
        } else if (this.targetSelection === OPTION_EXISTING) {
            this._saveExisting();
        } else if (this.targetSelection === OPTION_REJECT) {
            this._reject();
        }
    };

    this.downloadAttachment = function downloadAttachment(attachment) {
        tromanServer.Files.downloadAttachment(attachment.Id)
            .then(rsp => downloadService.downloadBlob(rsp.blob, rsp.filename));
    };

    this.$onInit = function $onInit() {
        $qservice
            .all({
                organizations: ObjectCache.getOrganizations(),
                wageCalculations: ObjectCache.getWageCalculations(),
                ceremony: tromanServer.Ceremonies.getCeremony(eventId),
            })
            .then(result => {
                if (result.ceremony.State === ceremonyStates.accepted) {
                    throw new TromanError('process-ceremony-already-accepted', 'Förrättningen har redan godkänts');
                }

                if (result.ceremony.State !== ceremonyStates.reported) {
                    throw new TromanError('process-ceremony-already-rejected', 'Förrättningen har dragits tillbaka');
                }

                this.organizations = result.organizations; // TODO include ceremony.Mission.MandatePeriod.Organization
                this.wageCalculations = result.wageCalculations; // TODO ceremony.Mission.MandatePeriod.Organization.WageCalculation
                /** @type {CeremonyDetails} */
                this.ceremony = result.ceremony;
                this.convertCeremonyModel = new ConvertCeremonyModel(result.ceremony);
                this.attachCeremonyModel = new AttachCeremonyModel(result.ceremony);
                this.targetSelection = OPTION_EXISTING;
                this.previousRejection = _.chain(this.ceremony.AuditEntries).filter(x => x.Incident === auditIncidents.ceremonyRejected).maxBy(x => x.Timestamp).value();
                this.claims = _.map(this.ceremony.Claims, x => ({ id: x.Id, title: getClaimTypeText(x.ClaimType), detail: getNuggetDetail(x.Nugget) }));

                stateService.guard(() => {
                    if (this.targetSelection === OPTION_NEW) {
                        return this.newform?.$dirty;
                    } else if (this.targetSelection === OPTION_EXISTING) {
                        return this.attachCeremonyModel?.hasChanges;
                    } else if (this.targetSelection === OPTION_REJECT) {
                        return this.rejectform?.$dirty;
                    }
                });
            })
            .catch(dialogService.error);
    };
}]);
